import { useEffect } from 'react';

import { useToastErrorHandler } from '@collab/libs/errors';
import { headerHeight } from '@collab/style/customThemeTokens';

import { AuthModalConfig } from 'libs/auth/useAuthProtected';
import useAuthorizeVolvoId from 'libs/volvoId/useAuthorizeVolvoId';
import useVolvoIdAccessToken from 'libs/volvoId/useVolvoIdAccessToken';

import GenerateAccessToken from './impl/GenerateAccessToken';
import { ApiOption } from './test-access-tokens.types';

const GenerateVolvoIdAccessToken: React.FC<{
  apiOptions: ApiOption[];
  scrollId: string;
  authProtectedConfig?: AuthModalConfig;
}> = ({ apiOptions, scrollId, authProtectedConfig }) => {
  const handleWithToast = useToastErrorHandler();
  const { isLoading, authorize } = useAuthorizeVolvoId();
  const {
    token,
    removeToken,
    isLoading: tokenLoading,
  } = useVolvoIdAccessToken();
  const saveScrollPos = useSessionScrollPosition(scrollId);

  const onAuthorize = async (apiId: string) => {
    saveScrollPos();

    try {
      await authorize(apiId);
    } catch (e) {
      handleWithToast(e, errorMsg);
    }
  };

  const onCloseModal = () => {
    removeToken();
  };

  return (
    <GenerateAccessToken
      tokenName="Volvo ID"
      generateButtonText="Authorise my car"
      apiOptions={apiOptions}
      onCloseModal={onCloseModal}
      onGenerate={onAuthorize}
      token={token}
      isGenerating={isLoading || tokenLoading}
      authProtectedConfig={authProtectedConfig}
    />
  );
};

const useSessionScrollPosition = (scrollId: string) => {
  const sessionKey = `${scrollId}_scrollPosition`;

  useEffect(() => {
    const scrollPos = sessionStorage.getItem(sessionKey);
    const positioningElement = document.querySelector(
      `#${scrollId}`,
    ) as HTMLSpanElement;

    if (scrollPos && positioningElement) {
      const scrollOffset = positioningElement.offsetTop - headerHeight.fromL;
      window.scrollTo(0, scrollOffset);
      sessionStorage.removeItem(sessionKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveScrollPos = () => {
    sessionStorage.setItem(sessionKey, 'true');
  };

  return saveScrollPos;
};

const errorMsg = {
  text: 'We could not generate your Volvo Id access token right now. Please try again later.',
  key: 'authorize-my-car',
};

export default GenerateVolvoIdAccessToken;
